import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import SEO from '../components/SEO';
import SafeIcon from '../common/SafeIcon';
import EstimateCalculator from '../components/EstimateCalculator';
import * as FiIcons from 'react-icons/fi';

const { 
  FiCpu, FiBarChart2, FiExternalLink, FiArrowRight, 
  FiInstagram, FiFacebook, FiZap, FiX, FiDollarSign, FiTarget 
} = FiIcons;

export default function Links() {
  const [isCalcOpen, setIsCalcOpen] = useState(false);

  // Helper to scroll to elements
  const scrollToCard = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  return (
    <>
      <SEO 
        title="Free Tools for Pet Businesses | Otso Co" 
        description="Quick wins to help you get more clients—without overwhelm. Free Pet Content Planner, Lead Funnel Audit, Pricing Calculator, and Ad Budget Calculator." 
        path="/links" 
      />
      
      <div className="min-h-screen bg-brand-white py-12 px-4 flex flex-col items-center">
        <div className="w-full max-w-[520px] space-y-10">
          
          {/* Top Section */}
          <div className="text-center space-y-4">
            <Link to="/" className="inline-block">
              <span className="text-2xl font-black tracking-tighter text-brand-primaryDark">Otso Co.</span>
            </Link>
            <div className="space-y-2">
              <h1 className="text-2xl md:text-3xl font-bold text-brand-primaryDark px-4">
                Free Tools for Pet Businesses
              </h1>
              <p className="text-gray-500 font-medium leading-relaxed px-6">
                Quick wins to help you get more clients—without overwhelm.
              </p>
            </div>
          </div>

          {/* Quick Nav Links */}
          <div className="flex flex-col items-center space-y-3 pt-2">
            <button 
              onClick={() => scrollToCard('content-planner')}
              className="text-sm font-bold text-brand-primaryDark/70 hover:text-brand-primaryDark transition-colors text-center"
            >
              Need content ideas? → <span className="underline decoration-brand-blush decoration-2 underline-offset-4">Pet Content Planner</span>
            </button>
            <button 
              onClick={() => scrollToCard('lead-funnel')}
              className="text-sm font-bold text-brand-primaryDark/70 hover:text-brand-primaryDark transition-colors text-center"
            >
              Not getting inquiries? → <span className="underline decoration-brand-mediumPurple decoration-2 underline-offset-4">Lead Funnel Score</span>
            </button>
            <button 
              onClick={() => scrollToCard('pricing-calc')}
              className="text-sm font-bold text-brand-primaryDark/70 hover:text-brand-primaryDark transition-colors text-center"
            >
              What should you charge? → <span className="underline decoration-brand-salmon decoration-2 underline-offset-4">Pricing Calculator</span>
            </button>
            <button 
              onClick={() => scrollToCard('ad-calc')}
              className="text-sm font-bold text-brand-primaryDark/70 hover:text-brand-primaryDark transition-colors text-center"
            >
              How much to spend on ads? → <span className="underline decoration-brand-lavender decoration-2 underline-offset-4">Ad Budget Calculator</span>
            </button>
            <button 
              onClick={() => scrollToCard('estimate-calc')}
              className="text-sm font-bold text-brand-primaryDark/70 hover:text-brand-primaryDark transition-colors text-center"
            >
              Want pricing? → <span className="underline decoration-brand-mauve decoration-2 underline-offset-4">Free Estimate</span>
            </button>
          </div>

          {/* Links/Cards Container */}
          <div className="space-y-6">
            
            {/* Card 1: Pet Content Planner */}
            <motion.div 
              id="content-planner"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-brand-blush/5 p-6 md:p-8 rounded-[32px] border border-brand-blush/20 flex flex-col items-center text-center"
            >
              <div className="w-14 h-14 bg-brand-blush/10 rounded-2xl flex items-center justify-center text-brand-primaryDark mb-6">
                <SafeIcon icon={FiCpu} size={28} />
              </div>
              <h2 className="text-xl font-bold text-brand-primaryDark mb-2">Pet Content Planner <span className="text-brand-mediumPurple font-medium text-sm">(Free)</span></h2>
              <p className="text-gray-600 mb-8 leading-relaxed text-sm">
                Paste your website + answer 3 questions to get a full week of social media content in 60 seconds.
              </p>
              <div className="w-full space-y-3">
                <a 
                  href="https://chatgpt.com/g/g-69a66c0ee14081918a59af31ad9cef4c-pet-content-planner" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-full bg-brand-primaryDark hover:bg-[#1f2552] text-white py-4 px-6 rounded-2xl font-bold text-lg transition-all shadow-lg flex items-center justify-center gap-2 group"
                >
                  Open Pet Content Planner <SafeIcon icon={FiExternalLink} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </a>
                <p className="text-[10px] text-center uppercase tracking-widest text-gray-400 font-bold">
                  Opens in ChatGPT (free account required)
                </p>
              </div>
            </motion.div>

            {/* Card 2: Lead Funnel Score */}
            <motion.div 
              id="lead-funnel"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-brand-mediumPurple/5 p-6 md:p-8 rounded-[32px] border border-brand-mediumPurple/10 flex flex-col items-center text-center"
            >
              <div className="w-14 h-14 bg-brand-mediumPurple/10 rounded-2xl flex items-center justify-center text-brand-mediumPurple mb-6">
                <SafeIcon icon={FiBarChart2} size={28} />
              </div>
              <h2 className="text-xl font-bold text-brand-primaryDark mb-2">Pet Business Lead Funnel Score <span className="text-brand-mediumPurple font-medium text-sm">(Free 2-Min Audit)</span></h2>
              <p className="text-gray-600 mb-8 leading-relaxed text-sm">
                Get a score (0–100), find your #1 bottleneck, and get a simple fix-this-week plan.
              </p>
              <Link 
                to="/lead-funnel-score"
                className="w-full bg-brand-mediumPurple hover:bg-brand-mauve text-white py-4 px-6 rounded-2xl font-bold text-lg transition-all shadow-lg flex items-center justify-center gap-2 group"
              >
                Take the 2-Minute Audit <SafeIcon icon={FiArrowRight} className="transition-transform group-hover:translate-x-1" />
              </Link>
            </motion.div>

            {/* Card 3: Pricing Calculator (NEW) */}
            <motion.div 
              id="pricing-calc"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-brand-salmon/5 p-6 md:p-8 rounded-[32px] border border-brand-salmon/20 flex flex-col items-center text-center"
            >
              <div className="w-14 h-14 bg-brand-salmon/10 rounded-2xl flex items-center justify-center text-brand-salmon mb-6">
                <SafeIcon icon={FiDollarSign} size={28} />
              </div>
              <h2 className="text-xl font-bold text-brand-primaryDark mb-2">Pet Care Pricing Calculator <span className="text-brand-salmon font-medium text-sm">(Free)</span></h2>
              <p className="text-gray-600 mb-8 leading-relaxed text-sm">
                Stop guessing. See how your rates compare to local market data and find your pricing sweet spot.
              </p>
              <Link 
                to="/pricing-calculator"
                className="w-full bg-brand-salmon hover:bg-[#e0917d] text-white py-4 px-6 rounded-2xl font-bold text-lg transition-all shadow-lg flex items-center justify-center gap-2 group"
              >
                Find My Ideal Rate <SafeIcon icon={FiArrowRight} className="transition-transform group-hover:translate-x-1" />
              </Link>
            </motion.div>

            {/* Card 4: Ad Budget Calculator (NEW) */}
            <motion.div 
              id="ad-calc"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-brand-lavender/5 p-6 md:p-8 rounded-[32px] border border-brand-lavender/20 flex flex-col items-center text-center"
            >
              <div className="w-14 h-14 bg-brand-lavender/10 rounded-2xl flex items-center justify-center text-brand-lavender mb-6">
                <SafeIcon icon={FiTarget} size={28} />
              </div>
              <h2 className="text-xl font-bold text-brand-primaryDark mb-2">Ad Budget Calculator <span className="text-brand-lavender font-medium text-sm">(Free)</span></h2>
              <p className="text-gray-600 mb-8 leading-relaxed text-sm">
                Calculate your Max CAC and ideal spend using the $100M Leads framework.
              </p>
              <Link 
                to="/ad-budget-calculator"
                className="w-full bg-brand-lavender hover:bg-brand-softPurple text-white py-4 px-6 rounded-2xl font-bold text-lg transition-all shadow-lg flex items-center justify-center gap-2 group"
              >
                Calculate My Ad Spend <SafeIcon icon={FiArrowRight} className="transition-transform group-hover:translate-x-1" />
              </Link>
            </motion.div>

            {/* Card 5: Estimate Calculator */}
            <motion.div 
              id="estimate-calc"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="bg-brand-mauve/5 p-6 md:p-8 rounded-[32px] border border-brand-mauve/20 flex flex-col items-center text-center"
            >
              <div className="w-14 h-14 bg-brand-mauve/10 rounded-2xl flex items-center justify-center text-brand-mauve mb-6">
                <SafeIcon icon={FiZap} size={28} />
              </div>
              <h2 className="text-xl font-bold text-brand-primaryDark mb-2">
                Free Social Media + Ads Estimate
              </h2>
              <p className="text-gray-600 mb-8 leading-relaxed text-sm">
                See your recommended monthly plan + suggested ad spend in 60 seconds.
              </p>
              <div className="w-full space-y-3">
                <button 
                  onClick={() => setIsCalcOpen(true)}
                  className="w-full bg-brand-mauve hover:bg-[#b07ba0] text-white py-4 px-6 rounded-2xl font-bold text-lg transition-all shadow-lg flex items-center justify-center gap-2 group"
                >
                  Get My Social + Ads Estimate <SafeIcon icon={FiArrowRight} className="transition-transform group-hover:translate-x-1" />
                </button>
                <p className="text-[10px] text-center uppercase tracking-widest text-gray-400 font-bold">
                  No call required.
                </p>
              </div>
            </motion.div>

          </div>

          {/* Social Links & Copyright */}
          <div className="pt-12 flex flex-col items-center space-y-8">
            <div className="flex items-center gap-8">
              <a 
                href="https://instagram.com/theperrydasilva" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-12 h-12 rounded-full bg-gray-50 border border-gray-100 flex items-center justify-center text-brand-primaryDark hover:bg-brand-blush/20 hover:text-brand-primaryDark transition-all"
                aria-label="Instagram"
              >
                <SafeIcon icon={FiInstagram} size={20} />
              </a>
              <a 
                href="https://facebook.com/theperrydasilva" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-12 h-12 rounded-full bg-gray-50 border border-gray-100 flex items-center justify-center text-brand-primaryDark hover:bg-brand-blush/20 hover:text-brand-primaryDark transition-all"
                aria-label="Facebook"
              >
                <SafeIcon icon={FiFacebook} size={20} />
              </a>
            </div>

            <div className="flex flex-col items-center gap-2">
              <div className="h-px w-12 bg-gray-100" />
              <p className="text-[11px] text-gray-400 font-bold uppercase tracking-[0.2em] pt-4">© {new Date().getFullYear()} Otso Co.</p>
            </div>
          </div>
        </div>
      </div>

      {/* Estimate Calculator Modal */}
      <AnimatePresence>
        {isCalcOpen && (
          <motion.div 
            initial={{ opacity: 0 }} 
            animate={{ opacity: 1 }} 
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[200] flex items-end md:items-center justify-center p-0 md:p-4"
          >
            <div className="absolute inset-0 bg-brand-primaryDark/80 backdrop-blur-sm" onClick={() => setIsCalcOpen(false)} />
            <motion.div 
              initial={{ y: '100%' }} 
              animate={{ y: 0 }} 
              exit={{ y: '100%' }}
              className="relative w-full max-w-2xl bg-white rounded-t-[32px] md:rounded-[32px] overflow-hidden shadow-2xl h-[90vh] md:h-auto md:max-h-[90vh]"
            >
              <EstimateCalculator isModal onClose={() => setIsCalcOpen(false)} />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}